import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { apiClient } from "@/common/apiClient";

export const UpdateKPIQuestionList = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [questionsText, setQuestionsText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    const fetchQuestionList = async () => {
      try {
        const response = await apiClient.get(`/api/kpi-question-list/${id}`);

        if (response.success) {
          const data = response.data;
          setTitle(data.title || "");
          setDescription(data.description || "");
          setStatus(data.status || "");

          // Convert questions array to text (one per line)
          const questionsAsText = data.questions
            ?.map((q: any) => q.text)
            .join("\n") || "";
          setQuestionsText(questionsAsText);
        } else {
          toast.error("Failed to load question list");
          navigate("/view-all-kpi-question-list");
        }
      } catch (error) {
        toast.error("Failed to load question list: " + (error as any).message);
        navigate("/view-all-kpi-question-list");
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchQuestionList();
    }
  }, [id, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const questions = questionsText
        .split("\n")
        .filter((line) => line.trim())
        .map((text) => ({ text: text.trim() }));

      if (questions.length === 0) {
        toast.error("Please add at least one question");
        return;
      }

      const response = await apiClient.put(`/api/kpi-question-list/${id}`, {
        title,
        description,
        questions,
      });

      if (response.success) {
        toast.success("Question list updated successfully!");
        navigate("/view-all-kpi-question-list");
      } else {
        toast.error("Failed to update question list: " + (response.message || "Unknown error"));
      }
    } catch (error) {
      toast.error("Failed to update question list: " + (error as any).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-6">
            <div className="text-center">Loading...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <CardTitle>Update KPI Question List</CardTitle>
          <CardDescription>
            Update the question list for KPI surveys
            {status === "published" && (
              <span className="text-yellow-600 block mt-2">
                ⚠️ Warning: This list is published. You cannot edit questions in published lists.
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g., Q1 Customer Satisfaction Survey"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                placeholder="Brief description of this survey"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="questions">Questions (One per line)</Label>
              <Textarea
                id="questions"
                placeholder="How satisfied are you with our service?
What can we improve?
Would you recommend us to others?"
                value={questionsText}
                onChange={(e) => setQuestionsText(e.target.value)}
                className="min-h-[200px] font-mono"
                required
                disabled={status === "published"}
              />
              <p className="text-sm text-muted-foreground">
                Enter each question on a new line
              </p>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/view-all-kpi-question-list")}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || status === "published"}
                className="flex-1"
                variant="default"
              >
                {isSubmitting ? "Updating..." : "Update Question List"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
